import React, { useMemo, useEffect } from "react";
import clsx from "clsx";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { getFormValues } from "redux-form";

import { Button, HookFormCheckbox } from "components";
import { AddressSectionHookForm } from "components/formSections";
import {
	AddressSectionFields,
	GoogleAddressOptionFields,
} from "globalConstants";
import { checkIsLatOrLngManual } from "helpers";
import { CountriesList, ShippingMethodTypes } from "types";

import ShippingDetails from "./shippingDetails";
import ShippingMethod from "./shippingMethod";

import styles from "../terminalSolution.module.scss";

type ShippingSectionProps = {
	sectionName?: string;
	isLoading: boolean;
	merchantInfo: any;
	isApplication?: boolean;
	businessFormName?: string;
};

const ShippingSection = ({
	sectionName = "shipping",
	isLoading,
	merchantInfo,
	isApplication = false,
	businessFormName = "businessForm",
}: ShippingSectionProps) => {
	const { register, watch, setValue, unregister, clearErrors } =
		useFormContext();

	const { legalAddress, businessAddress, shippingAddress } = merchantInfo;

	const shippingMethodType = watch(`${sectionName}.shippingMethod`);

	const isShippingAddressExists = useMemo(
		() =>
			shippingAddress?.address_line1 ||
			shippingAddress?.address_line2 ||
			shippingAddress?.region_code ||
			shippingAddress?.city ||
			shippingAddress?.postal_code,
		[shippingAddress],
	);

	const businessFormValues: any = useSelector(state =>
		getFormValues(businessFormName)(state),
	);

	const getAddressFieldName = name => `${sectionName}.address.${name}`;

	const setAddressFields = address => {
		const addressFields = address?.[GoogleAddressOptionFields.Address];

		setValue(
			getAddressFieldName(AddressSectionFields.SearchAddress.name),
			addressFields?.[AddressSectionFields.SearchAddress.name] || "",
			{
				shouldValidate: !checkIsLatOrLngManual({
					lat: addressFields?.[GoogleAddressOptionFields.Lat],
					lng: addressFields?.[GoogleAddressOptionFields.Lng],
				}),
				shouldDirty: true,
			},
		);
		setValue(
			getAddressFieldName(AddressSectionFields.Country.name),
			CountriesList.getByValue(
				addressFields?.[AddressSectionFields.CountryCode.name],
			) || "",
			{ shouldValidate: true },
		);

		[
			AddressSectionFields.Street.name,
			AddressSectionFields.Apartments.name,
			AddressSectionFields.City.name,
			AddressSectionFields.Region.name,
			AddressSectionFields.PostalCode.name,
			GoogleAddressOptionFields.Lat,
			GoogleAddressOptionFields.Lng,
		].forEach(name => {
			setValue(getAddressFieldName(name), addressFields?.[name] || "", {
				shouldValidate: true,
			});
		});
	};

	const handleCopyFromLegal = () => {
		setAddressFields(legalAddress);
	};

	const handleCopyFromDba = () => {
		setAddressFields(businessAddress);
	};

	const handleCopyFromLocation = () => {
		setAddressFields(businessFormValues?.businessAddress);
		setValue(
			`${sectionName}.phoneNumber`,
			businessFormValues?.businessPhone || "",
			{
				shouldValidate: true,
			},
		);
	};

	useEffect(() => {
		if (shippingMethodType === ShippingMethodTypes.none.value?.toString()) {
			unregister([
				`${sectionName}.addressLine1`,
				`${sectionName}.city`,
				`${sectionName}.regionCode`,
				`${sectionName}.postalCode`,
				`${sectionName}.country`,
			]);
			clearErrors(sectionName);
		}
	}, [clearErrors, sectionName, shippingMethodType, unregister]);

	return (
		<div data-autotest="shippingSection">
			<hr className={clsx(styles.divider, "mt-3 mb-4")} />
			<p className={styles.subHeader}>Shipping</p>
			<ShippingMethod sectionName={sectionName} isLoading={isLoading} />
			{shippingMethodType !==
			ShippingMethodTypes.none.value?.toString() ? (
				<>
					<hr className={clsx(styles.divider, "mt-3 mb-4")} />
					{!isShippingAddressExists && !isApplication && (
						<Row className="form-group">
							<Col sm={12}>
								<Button
									onClick={handleCopyFromLegal}
									value="Copy from legal address"
									variant="secondary"
									className="pl-0"
								/>
								<Button
									onClick={handleCopyFromDba}
									value="Copy from DBA address"
									variant="secondary"
								/>
							</Col>
						</Row>
					)}
					{isApplication && (
						<>
							<Row className="form-group">
								<Col sm={12}>
									<Button
										onClick={handleCopyFromLocation}
										value="Copy from location"
										variant="secondary"
										className="pl-0"
									/>
								</Col>
							</Row>
							<Row className="form-group">
								<Col sm={12}>
									<HookFormCheckbox
										register={register}
										id={`${sectionName}.shouldCopyShippingAddressToMerchant`}
										name={`${sectionName}.shouldCopyShippingAddressToMerchant`}
										label="Save shipping address to merchant information"
										blurLoading={isLoading}
										className="mb-3"
									/>
								</Col>
							</Row>
						</>
					)}
					<AddressSectionHookForm
						sectionName={`${sectionName}.address`}
						isTypeOfLocationRequired
						isRequired
					/>
					<ShippingDetails
						sectionName={sectionName}
						isLoading={isLoading}
						isApplication={isApplication}
					/>
				</>
			) : null}
		</div>
	);
};

export default ShippingSection;
